/**
 * Collection Infinite Scroll
 * VERSION: 1.3 (fixed)
 *
 * Progressively loads additional pages of a collection grid as the user
 * scrolls, using Shopify's Section Rendering API (?section_id=...).
 * Falls back gracefully to the theme's native <server-rendered> pagination
 * links if JS fails or is unavailable (those links are always rendered
 * server-side and only hidden once this script confirms it can take over).
 *
 * Re-initializes automatically after the theme's filter/sort AJAX reload
 * (listens for the 'collection:reloaded' event dispatched by theme.js).
 *
 * FIX (v1.1): v1.0 disconnected the observer permanently after a single
 * failed/empty fetch (network hiccup, a third-party app script mutating
 * the DOM mid-parse, a slow/empty response, etc.). Because collections
 * with several pages of products only need ONE bad response to get stuck,
 * this silently stranded real, in-stock products beyond whatever page
 * happened to load before the failure — exactly what was observed on
 * /collections/shop-all, where products past the first ~2 loaded pages
 * (e.g. "Artline Silica Gel, 2kg") never appeared while scrolling, even
 * though the collection genuinely contains them.
 *
 * This version retries transient failures instead of giving up on the
 * first one, only treats a response as "end of list" when the server
 * unambiguously reports we're on/past the last page, and logs failures to
 * the console so they're diagnosable instead of silent.
 *
 * FIX (v1.2): even after the v1.1 retry fix, testing showed the root
 * trigger was the actual problem: the IntersectionObserver watching the
 * sentinel sometimes never fires a single callback — not even its
 * mandatory initial one — while the sentinel is demonstrably sitting
 * inside the viewport. IntersectionObserver callbacks are timed to the
 * rendering pipeline, and delivery can be skipped or indefinitely delayed
 * by things entirely outside this script's control: a busy main thread
 * from the many third-party embeds this store loads (review widgets,
 * pixels, chat, upsell apps), a backgrounded/occluded tab, or a rare
 * browser bug. In that state, no fetch is ever attempted and the customer
 * is stuck on page 1 forever with no error, even though loadNextPage()
 * works perfectly the instant it's actually called.
 *
 * This version no longer relies on IntersectionObserver alone. It adds a
 * throttled scroll/resize fallback that directly measures the sentinel's
 * position on screen and calls loadNextPage() itself once the sentinel is
 * within range — independent of whether IntersectionObserver ever
 * delivers a callback. IntersectionObserver is kept as a (cheap,
 * efficient) first line, but the fallback guarantees loading still
 * happens even when it doesn't fire.
 *
 * FIX (v1.3) — THE actual root cause of missing products (confirmed by
 * direct testing): onPageFetched iterated `newGrid.children` — a LIVE
 * HTMLCollection — while simultaneously removing each item from it via
 * `fragment.appendChild(child)` (appendChild moves a node, which detaches
 * it from its previous parent). Removing items from a live collection
 * *while iterating it* shifts every later index down by one per removal,
 * so a plain `forEach`/loop over it silently skips roughly every other
 * item. Verified directly against this collection: the fetched page's
 * grid had 54 child nodes but only 27 actually made it into the fragment
 * — an exact 50% loss — which is why "Artline Silica Gel, 2kg" (and many
 * other real, in-stock products) never appeared even once infinite
 * scroll was correctly firing and fetching. The fix: snapshot the
 * children into a plain array first, then move each one from the array
 * — mutating the live collection mid-iteration is no longer possible.
 */
(function () {
  'use strict';

  var MAX_RETRIES = 3;
  var RETRY_DELAY_MS = 800;
  var SCROLL_CHECK_THROTTLE_MS = 200;
  var VIEWPORT_LOAD_MARGIN_PX = 600;

  function CollectionInfiniteScroll(container) {
    this.container = container;
    this.grid = container.querySelector('.collection-grid__wrapper .grid.grid--uniform') ||
      container.querySelector('.grid.grid--uniform');
    this.pagination = container.querySelector('.pagination');
    this.sentinel = container.querySelector('#InfiniteScrollSentinel');
    this.loadingEl = container.querySelector('#InfiniteScrollLoading');
    this.sectionId = container.dataset.sectionId;
    this.isLoading = false;
    this.retryCount = 0;
    this.observer = null;
    this.scrollFallbackActive = false;
    this.boundScrollCheck = this.throttledScrollCheck.bind(this);
    this.scrollCheckTimer = null;

    this.init();
  }

  CollectionInfiniteScroll.prototype.init = function () {
    this.readState();
    this.disconnectObserver();
    this.retryCount = 0;

    if (!this.grid || !this.sentinel || !this.sectionId) {
      if (window.console && console.warn) {
        console.warn(
          '[collection-infinite-scroll] Missing required element(s) — staying on server-rendered pagination.',
          { grid: !!this.grid, sentinel: !!this.sentinel, sectionId: this.sectionId }
        );
      }
      return;
    }

    // Hide the classic numbered pagination as soon as JS is in control —
    // regardless of which page we happen to have landed on directly (e.g.
    // a deep link, a reload, or browser back/forward to ?page=4). There is
    // never a good reason to show page numbers once infinite scroll is
    // active; if we're already on the last page we simply skip observing
    // (nothing left to load) but the pager still stays hidden.
    this.container.classList.add('is-infinite-active');

    if (this.currentPage >= this.totalPages) {
      return;
    }

    this.observeSentinel();
    this.enableScrollFallback();
  };

  CollectionInfiniteScroll.prototype.readState = function () {
    this.currentPage = parseInt(this.container.dataset.currentPage, 10) || 1;
    this.totalPages = parseInt(this.container.dataset.totalPages, 10) || 1;
  };

  CollectionInfiniteScroll.prototype.observeSentinel = function () {
    var self = this;

    this.disconnectObserver();

    this.observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting && !self.isLoading) {
          self.loadNextPage();
        }
      });
    }, { rootMargin: '600px 0px 600px 0px' });

    this.observer.observe(this.sentinel);
  };

  CollectionInfiniteScroll.prototype.disconnectObserver = function () {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    this.disableScrollFallback();
  };

  // Belt-and-braces fallback: don't rely solely on IntersectionObserver
  // callbacks actually being delivered (see FIX v1.2 note above). Poll the
  // sentinel's real screen position on scroll/resize, throttled so it's
  // cheap, and load the next page ourselves the moment it's in range.
  CollectionInfiniteScroll.prototype.enableScrollFallback = function () {
    if (this.scrollFallbackActive) return;
    this.scrollFallbackActive = true;
    window.addEventListener('scroll', this.boundScrollCheck, { passive: true });
    window.addEventListener('resize', this.boundScrollCheck, { passive: true });
    // Check once immediately in case the sentinel is already on-screen
    // (e.g. a short page/collection where there's nothing to scroll).
    this.checkSentinelPosition();
  };

  CollectionInfiniteScroll.prototype.disableScrollFallback = function () {
    if (!this.scrollFallbackActive) return;
    this.scrollFallbackActive = false;
    window.removeEventListener('scroll', this.boundScrollCheck);
    window.removeEventListener('resize', this.boundScrollCheck);
    if (this.scrollCheckTimer) {
      clearTimeout(this.scrollCheckTimer);
      this.scrollCheckTimer = null;
    }
  };

  CollectionInfiniteScroll.prototype.throttledScrollCheck = function () {
    var self = this;
    if (this.scrollCheckTimer) return;
    this.scrollCheckTimer = window.setTimeout(function () {
      self.scrollCheckTimer = null;
      self.checkSentinelPosition();
    }, SCROLL_CHECK_THROTTLE_MS);
  };

  CollectionInfiniteScroll.prototype.checkSentinelPosition = function () {
    if (this.isLoading || this.currentPage >= this.totalPages || !this.sentinel || !this.sentinel.isConnected) {
      return;
    }
    var rect = this.sentinel.getBoundingClientRect();
    var viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    if (rect.top <= viewportHeight + VIEWPORT_LOAD_MARGIN_PX) {
      this.loadNextPage();
    }
  };

  CollectionInfiniteScroll.prototype.loadNextPage = function () {
    if (this.isLoading || this.currentPage >= this.totalPages) {
      return;
    }

    this.isLoading = true;
    if (this.loadingEl) this.loadingEl.hidden = false;

    var nextPage = this.currentPage + 1;
    var params = new URLSearchParams(window.location.search);
    params.set('page', nextPage);
    params.set('section_id', this.sectionId);

    var url = window.location.pathname + '?' + params.toString();

    fetch(url, { credentials: 'same-origin' })
      .then(function (response) {
        if (!response.ok) throw new Error('Bad response: ' + response.status);
        return response.text();
      })
      .then(this.onPageFetched.bind(this, nextPage))
      .catch(this.onError.bind(this, nextPage));
  };

  CollectionInfiniteScroll.prototype.onPageFetched = function (nextPage, html) {
    var parser = new DOMParser();
    var newDom = parser.parseFromString(html, 'text/html');
    var newContainer = this.container.id ? newDom.getElementById(this.container.id) : null;
    var newGrid = newContainer
      ? (newContainer.querySelector('.collection-grid__wrapper .grid.grid--uniform') || newContainer.querySelector('.grid.grid--uniform'))
      : null;

    // Only treat this as "nothing more to load" when we can positively
    // confirm it from the server's own pagination state (current page is
    // now at/after total pages). A response we simply failed to parse, or
    // that came back briefly empty/malformed for any other reason, is a
    // transient failure and gets retried instead — previously this branch
    // fired on ANY missing/empty grid and permanently stopped scrolling,
    // stranding every product on later pages.
    var reportedCurrentPage = newContainer ? parseInt(newContainer.dataset.currentPage, 10) : NaN;
    var reportedTotalPages = newContainer ? parseInt(newContainer.dataset.totalPages, 10) : NaN;
    var serverConfirmsEnd = !isNaN(reportedCurrentPage) && !isNaN(reportedTotalPages) &&
      reportedCurrentPage >= reportedTotalPages;

    if (!newGrid || !newGrid.children.length) {
      if (serverConfirmsEnd) {
        this.currentPage = reportedTotalPages;
        this.totalPages = reportedTotalPages;
        this.isLoading = false;
        if (this.loadingEl) this.loadingEl.hidden = true;
        this.disconnectObserver();
        return;
      }

      if (window.console && console.warn) {
        console.warn(
          '[collection-infinite-scroll] Page ' + nextPage + ' came back without a parseable product grid — retrying.',
          { foundContainer: !!newContainer, retryCount: this.retryCount }
        );
      }
      this.retryLoad(nextPage);
      return;
    }

    // IMPORTANT: snapshot into a static array before moving anything.
    // newGrid.children is a *live* HTMLCollection — appendChild() below
    // removes each node from it as we go, which reindexes the live
    // collection mid-loop and silently skips every other item if you
    // iterate the live collection directly. This was the actual cause of
    // products (including this one) disappearing during infinite scroll.
    var childrenSnapshot = Array.prototype.slice.call(newGrid.children);
    var fragment = document.createDocumentFragment();
    var newItems = [];
    childrenSnapshot.forEach(function (child) {
      newItems.push(child);
      fragment.appendChild(child);
    });
    this.grid.appendChild(fragment);

    this.retryCount = 0;

    if (newContainer && !isNaN(reportedCurrentPage) && !isNaN(reportedTotalPages)) {
      this.currentPage = reportedCurrentPage;
      this.totalPages = reportedTotalPages;
      this.container.dataset.currentPage = this.currentPage;
      this.container.dataset.totalPages = this.totalPages;
    } else {
      this.currentPage = nextPage;
    }

    this.reinitNewItems(newItems);

    this.isLoading = false;
    if (this.loadingEl) this.loadingEl.hidden = true;

    if (this.currentPage >= this.totalPages) {
      this.disconnectObserver();
    } else {
      // Re-attach the observer to the same sentinel node in case a
      // third-party script (review widgets, pixels, upsell apps — all
      // common on product/collection pages) detached and re-inserted it
      // during the DOM update above, which can silently stop delivering
      // IntersectionObserver callbacks in some browsers.
      this.observeSentinel();
      this.enableScrollFallback();
    }

    document.dispatchEvent(new CustomEvent('collection:infinite-scroll-loaded', {
      detail: { page: this.currentPage, items: newItems }
    }));
  };

  CollectionInfiniteScroll.prototype.retryLoad = function (nextPage) {
    var self = this;
    this.retryCount++;

    if (this.retryCount > MAX_RETRIES) {
      if (window.console && console.error) {
        console.error(
          '[collection-infinite-scroll] Giving up on page ' + nextPage + ' after ' + MAX_RETRIES +
          ' retries — falling back to visible pagination so customers can still reach later pages.'
        );
      }
      this.isLoading = false;
      if (this.loadingEl) this.loadingEl.hidden = true;
      this.disconnectObserver();
      this.container.classList.remove('is-infinite-active');
      return;
    }

    this.isLoading = false;
    window.setTimeout(function () {
      self.isLoading = false;
      self.loadNextPage();
    }, RETRY_DELAY_MS * this.retryCount);
  };

  CollectionInfiniteScroll.prototype.reinitNewItems = function (items) {
    if (!items.length) return;

    function run() {
      items.forEach(function (item) {
        window.theme.reinitProductGridItem(item);
      });
      if (window.AOS) window.AOS.refreshHard();
    }

    if (window.theme && window.theme.reinitProductGridItem) {
      run();
      return;
    }

    // theme.js may still be lazy-loading (see layout/theme.liquid) — poll
    // briefly using the same pattern already used elsewhere in the theme.
    var tries = 0;
    var iv = setInterval(function () {
      tries++;
      if (window.theme && window.theme.reinitProductGridItem) {
        clearInterval(iv);
        run();
      } else if (tries > 150) {
        clearInterval(iv);
      }
    }, 30);
  };

  CollectionInfiniteScroll.prototype.onError = function (nextPage, err) {
    if (window.console && console.warn) {
      console.warn('[collection-infinite-scroll] Fetch failed for page ' + nextPage + ' — retrying.', err);
    }
    this.retryLoad(nextPage);
  };

  function initAll() {
    document.querySelectorAll('[data-infinite-scroll]').forEach(function (container) {
      if (container.infiniteScrollInstance) {
        container.infiniteScrollInstance.disconnectObserver();
      }
      container.infiniteScrollInstance = new CollectionInfiniteScroll(container);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Re-run after the theme's own AJAX filter/sort reload swaps the DOM back
  // to a fresh page 1.
  document.addEventListener('collection:reloaded', initAll);

  window.theme = window.theme || {};
  window.theme.CollectionInfiniteScroll = CollectionInfiniteScroll;
})();
